import axios from "axios";
import endpoints from "../config/endpoints";
import { IProduct } from "../models/product.model";

let favoriteIdsCache: Set<number> | null = null;
let favoriteIdsPromise: Promise<Set<number>> | null = null;

const getCookie = (name: string) => {
    if (typeof document === "undefined") {
        return null;
    }
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return parts.pop()?.split(";").shift();
    }
    return null;
};

const getAuthHeaders = () => {
    const token = getCookie("apinew-token") || getCookie("api-token");
    return token ? { Authorization: `Bearer ${token}` } : {};
};

export async function getFavoriteProductIds(force = false): Promise<Set<number>> {
    if (!force && favoriteIdsCache) {
        return favoriteIdsCache;
    }
    if (!force && favoriteIdsPromise) {
        return favoriteIdsPromise;
    }

    favoriteIdsPromise = axios
        .get(endpoints.productFavorites.ids, {
            headers: getAuthHeaders(),
        })
        .then((response) => {
            const ids = response?.data?.productIds || [];
            favoriteIdsCache = new Set(ids.map((id: number | string) => Number(id)));
            return favoriteIdsCache;
        })
        .finally(() => {
            favoriteIdsPromise = null;
        });

    return favoriteIdsPromise;
}

export async function getFavoriteProducts(): Promise<IProduct[]> {
    const response = await axios.get(endpoints.productFavorites.list, {
        headers: getAuthHeaders(),
    });
    const items = response?.data?.items || [];
    favoriteIdsCache = new Set(items.map((product: IProduct) => Number(product.productId)));
    return items;
}

export async function toggleFavoriteProduct(productId: number, source = "generator"): Promise<boolean> {
    const response = await axios.post(
        endpoints.productFavorites.toggle,
        { productId, source },
        {
            headers: getAuthHeaders(),
        }
    );
    const isFavorite = Boolean(response?.data?.isFavorite);

    if (!favoriteIdsCache) {
        favoriteIdsCache = new Set();
    }
    if (isFavorite) {
        favoriteIdsCache.add(productId);
    } else {
        favoriteIdsCache.delete(productId);
    }

    return isFavorite;
}

export async function removeFavoriteProduct(productId: number): Promise<void> {
    await axios.delete(endpoints.productFavorites.delete(productId), {
        headers: getAuthHeaders(),
    });
    if (favoriteIdsCache) {
        favoriteIdsCache.delete(productId);
    }
}
